import { Select } from "antd";

export interface FilterSelectOption {
  value: string;
  label: string;
}

interface FilterSelectProps {
  /** Lands on a wrapper `<div>`, never the Select — see the note below. */
  "data-cy"?: string;
  placeholder?: string;
  value?: string;
  onChange: (value?: string) => void;
  options: FilterSelectOption[];
  /** Tailwind width classes for the wrapper. Defaults to a toolbar-sized field. */
  className?: string;
}

/**
 * Sizes this Select to 36px so it lines up with `CustomSearchBar` beside it.
 *
 * The app's ConfigProvider sets `controlHeight: 40`, so AntD offers 40 / 48 / 32
 * and no 36 — while CustomSearchBar pins itself to `h-9`. Overriding it needs
 * `!important` on every declaration, because AntD 5 injects component styles as
 * runtime CSS-in-JS that wins on load order regardless of selector specificity.
 *
 * It also needs the OUTER element, not only `.ant-select-selector`. AntD's rule
 * is `.ant-select-single { height: 40px }`, so styling the selector alone yields
 * a 36px control centred in a 40px box — visibly 4px off the search bar, which is
 * how this shipped twice before being caught in DevTools.
 *
 * Kept as arbitrary variants on the wrapper rather than in a stylesheet for a
 * concrete reason: Sass cannot parse Tailwind's `!` prefix inside `@apply`
 * (`@apply !h-9` is a parse error), so an SCSS version has to hand-write the
 * declarations and drifts from the utility scale. This is the same approach, and
 * the same values, as `src/features/logistics-upaya/components/TicketFilters.tsx`,
 * which hit this first.
 *
 * The inner `<input>` is deliberately 34px, not 36px: AntD lets it fill the
 * wrapper on its own, and pinning it to the full height pushes the placeholder
 * off-centre.
 */
const H36 = [
  // The OUTER element carries AntD's height, not just the selector: its rule is
  // `.ant-select-single { height: 40px }`. Overriding only the selector left a
  // 36px control inside a 40px box, so the visible border still sat 4px off the
  // search bar beside it — which is what the first two attempts here missed.
  "[&_.ant-select]:!h-9",
  "[&_.ant-select-selector]:!h-9",
  // Centre the selector inside its box so the text position stops depending on
  // line-height arithmetic matching the height exactly.
  "[&_.ant-select]:!flex",
  "[&_.ant-select]:!items-center",
  "[&_.ant-select-selection-item]:!leading-9",
  "[&_.ant-select-selection-placeholder]:!leading-9",
  "[&_.ant-select-selection-search-input]:!h-[34px]",
].join(" ");

/**
 * A single-select filter sized to sit beside `CustomSearchBar` in a toolbar row.
 *
 * Two things every call site was otherwise re-solving:
 *
 * 1. **Height** — see above.
 * 2. **`data-cy` placement.** AntD renders the dropdown in a portal, so an
 *    attribute on the Select itself cannot scope the options. It goes on a
 *    wrapper `<div>`, and tests query the options from the document root.
 *
 * `allowClear` is always on: a filter you cannot un-set is a trap, and clearing
 * reports `undefined` rather than `""` so the caller can drop the parameter from
 * a request entirely.
 */
export default function FilterSelect({
  "data-cy": dataCy,
  placeholder = "All",
  value,
  onChange,
  options,
  className = "w-full sm:w-44",
}: FilterSelectProps) {
  return (
    <div data-cy={dataCy} className={`${className} ${H36}`}>
      <Select
        allowClear
        className="w-full"
        placeholder={placeholder}
        value={value}
        // AntD hands `undefined` on clear; normalising here means no call site
        // has to remember to, and none can accidentally send `?status=`.
        onChange={(next) => onChange(next ?? undefined)}
        options={options}
      />
    </div>
  );
}
